@if (subscriptions?.length && currentSubscription) {
  <div class="flex flex-col gap-6">
    <div class="mt-6 flex flex-wrap justify-center gap-3 lg:flex-nowrap">
      <div class="flex flex-wrap gap-3 md:flex-nowrap">
        <div class="flex flex-wrap gap-3 md:flex-nowrap">
          <span class="font-semibold text-default" [ngClass]="{ 'opacity-50': yearlyBillingToggle.disabled }">
            {{ 'Pay monthly' | translate }}
          </span>
          <sb-switch [formControl]="yearlyBillingToggle" [ariaLabel]="'Toggle yearly billing' | translate" />
          <span class="font-semibold" [ngClass]="{ 'opacity-50': yearlyBillingToggle.disabled }">
            {{ 'Pay yearly' | translate }}
          </span>
        </div>
        <sb-badge [color]="'brand'" [ngClass]="{ 'opacity-50': yearlyBillingToggle.disabled }">
          {{ 'Save up to 10%' | translate }}
        </sb-badge>
      </div>
    </div>
    @if (shouldHidePlansForEntitledSubscription) {
      <sb-notice variant="info">
        <p
          [innerHTML]="
            'You are currently on a new subscription plan. To make changes to your subscription, please contact our support team at [email]'
              | translate: { email: 'support@shiftbase.com' | mailto }
          "
        ></p>
      </sb-notice>
      <sb-plan-card
        [name]="currentSubscription.plan.name"
        [displayPrice]="currentSubscription.display_price"
        [isCurrent]="true"
        [selected]="true"
        [display]="'horizontal'"
      ></sb-plan-card>
    } @else if (hasEnterprisePlan) {
      <sb-notice variant="info">
        <p
          [innerHTML]="
            'Do you want to change your plan? Contact sales, send an email to [email]'
              | translate: { email: 'enterprise@shiftbase.com' | mailto }
          "
        ></p>
      </sb-notice>
      <sb-plan-card
        [name]="currentSubscription.plan.name"
        [displayPrice]="currentSubscription.display_price"
        [isCurrent]="true"
        [selected]="true"
        [display]="'horizontal'"
      ></sb-plan-card>
    } @else {
      <div
        class="grid grid-cols-1 gap-3 lg:gap-6"
        [ngClass]="{
          'lg:grid-cols-2': availableSubscriptions.length === 2,
          'lg:grid-cols-3': availableSubscriptions.length > 2,
        }"
      >
        @for (subscription of availableSubscriptions; track subscription.id) {
          <sb-plan-card
            [mostPopular]="planFeatures[subscription.plan.weight].mostPopular"
            [selected]="
              selectedSubscription
                ? selectedSubscription?.id === subscription.id
                : currentSubscription?.id === subscription.id
            "
            [isCurrent]="(currentPlanId() ?? currentSubscription?.id) === subscription.id"
            [inTrial]="inTrialPeriod"
            [isFree]="subscription.price === 0"
            [name]="subscription.plan.name"
            [tagline]="planFeatures[subscription.plan.weight].tagline | translate"
            [disabled]="determineDisabledStateForPlanCards(subscription)"
            [displayPrice]="getDisplayPrice(subscription)"
            [minOrderQuantity]="subscription.min_order_quantity"
            [featureTitle]="planFeatures[subscription.plan.weight].featureTitle | translate"
            [features]="planFeatures[subscription.plan.weight].features"
            [featureParams]="getFeatureTranslationParams(subscription)"
            [brandColor]="planFeatures[subscription.plan.weight].brandColor"
            [useNewPricingStructure]="planFeatures[subscription.plan.weight].useNewPricingStructure"
            [additionalEmployeeCost]="getAdditionalEmployeeCost(subscription)"
            (selectPlan)="selectSubscription(subscription)"
            [trialGranted]="trialGranted"
            [trialExpired]="trialExpired"
          >
            <ng-template #disabledTooltip>
              <div class="flex flex-col gap-2">
                <span>{{
                  'You cannot select the [subscription] subscription:'
                    | translate: { subscription: subscription.plan.name }
                }}</span>
                @for (quota of subscription.quotaReached; track quota) {
                  <span
                    >{{
                      quota.limit > 0
                        ? ('You have [usage] [name]. The [subscription] subscription includes a maximum of [limit] [name].'
                          | translate
                            : {
                                usage: quota.usage,
                                name: quota.name,
                                subscription: subscription.plan.name,
                                limit: quota.limit,
                              })
                        : ('You have [usage] [name]. The [subscription] subscription includes no [name].'
                          | translate
                            : {
                                usage: quota.usage,
                                name: quota.name,
                                subscription: subscription.plan.name,
                              })
                    }}
                  </span>
                }
              </div>
            </ng-template>
          </sb-plan-card>
        }
      </div>
    }

    @if (shouldShowLegacyPlanBox && !shouldHidePlansForEntitledSubscription) {
      <sb-plan-card
        [name]="currentSubscription.plan.name"
        [displayPrice]="currentSubscription.display_price"
        [isCurrent]="true"
        [selected]="selectedSubscription?.id === currentSubscription.id"
        [display]="'horizontal'"
        [isLegacyPlan]="true"
      ></sb-plan-card>
    }
  </div>
}

@if (subscriptions?.length && !shouldHidePlansForEntitledSubscription) {
  <sb-addon-card
    [class.opacity-50]="selectedSubscription?.plan?.weight === planTypeWeight[planType.FREE]"
    [title]="('Add App Center Plus for ' | translate) + (integrationPlusPrice | subscriptionCurrency)"
    [description]="
      'With App Center Plus, in addition to our free integrations, you also get access to all \'Plus\' integrations.'
        | translate
    "
    [pricingModel]="'flat_fee'"
    [imgSrc]="'/assets/images/app-center-plus-logo.svg'"
  >
    <sb-switch
      [formControl]="integrationPlus"
      [sbTooltip]="integrationPlusTooltip"
      [sbTooltipDisabled]="!integrationPlus.disabled"
      [sbTooltipPlacement]="'top-left'"
      [ariaLabel]="'Toggle App Center Plus' | translate"
    />
    <ng-template #integrationPlusTooltip>
      @if (hasEnterprisePlan) {
        <span>{{ 'App Center Plus is included in your plan' | translate }}</span>
      } @else {
        <span>
          <span class="font-bold">{{ 'App Center Plus' | translate }}</span>
          {{ 'is only available within our' | translate }}
          <span class="font-bold">Basic</span>
          {{ 'and' | translate }}
          <span class="font-bold">Premium</span>
          {{ 'plans' | translate }}.
          {{ 'Upgrade now, and get access to all our integrations!' | translate }}
        </span>
      }
    </ng-template>
  </sb-addon-card>

  @if (featureCompliancePlusAddonEnabled) {
    <sb-addon-card
      [class.opacity-50]="selectedSubscription?.plan?.weight === planTypeWeight[planType.FREE]"
      [title]="'Add Compliance+ for [price]' | translate: { price: compliancePlusPrice | subscriptionCurrency }"
      [description]="
        'With Compliance+, you get real-time alerts and tools to prevent and resolve scheduling violations.' | translate
      "
      [pricingModel]="'per_unit'"
      [imgSrc]="'/assets/images/addon/compliance.svg'"
    >
      <sb-switch
        [formControl]="compliancePlus"
        [sbTooltip]="compliancePlusTooltip"
        [sbTooltipDisabled]="!compliancePlus.disabled"
        [sbTooltipPlacement]="'top-left'"
        [ariaLabel]="'Toggle Compliance+' | translate"
      />
      <ng-template #compliancePlusTooltip>
        @if (hasEnterprisePlan) {
          {{ 'Compliance+ is included in your plan' | translate }}
        } @else {
          {{
            'Compliance+ is only available within our Basic and Premium plans. Upgrade now, and get access to real-time alerts and tools to prevent and resolve scheduling violations.'
              | translate
          }}
        }
      </ng-template>
    </sb-addon-card>
  }

  @if (featureHrProAddonEnabled) {
    <sb-addon-card
      [class.opacity-50]="selectedSubscription?.plan?.weight === planTypeWeight[planType.FREE]"
      [title]="'Add HR Pro for [price]' | translate: { price: hrPlusPrice | subscriptionCurrency }"
      [description]="
        'With HR Pro, you get a complete HR toolkit: digital contracts, employee records, and document management.'
          | translate
      "
      [caption]="'Promotional rate until 1 January 2027.' | translate"
      [pricingModel]="'per_unit'"
      [imgSrc]="'/assets/images/addon/hr-pro.svg'"
    >
      <sb-switch
        [formControl]="hrPlus"
        [sbTooltip]="hrPlusTooltip"
        [sbTooltipDisabled]="!hrPlus.disabled"
        [sbTooltipPlacement]="'top-left'"
        [ariaLabel]="'Toggle HR Pro' | translate"
      />
      <ng-template #hrPlusTooltip>
        {{ 'HR Pro is only available within our Basic and Premium plans.' | translate }}
      </ng-template>
    </sb-addon-card>
  }

  @if (featureChatAddonEnabled) {
    <sb-addon-card
      [class.opacity-50]="selectedSubscription?.plan?.weight === planTypeWeight[planType.FREE]"
      [title]="'Add Chat for [price]' | translate: { price: chatPrice | subscriptionCurrency }"
      [description]="'With Chat, your team communicates in real time, directly inside Shiftbase.' | translate"
      [pricingModel]="'per_unit'"
      [imgSrc]="'/assets/images/addon/chat.svg'"
    >
      <sb-switch
        [formControl]="chat"
        [sbTooltip]="chatTooltip"
        [sbTooltipDisabled]="!chat.disabled"
        [sbTooltipPlacement]="'top-left'"
        [ariaLabel]="'Toggle Chat' | translate"
      />
      <ng-template #chatTooltip>
        {{ 'Chat is only available within our Basic and Premium plans.' | translate }}
      </ng-template>
    </sb-addon-card>
  }
}

<div class="mt-6 flex flex-col items-center gap-4">
  <a
    class="cursor-pointer border-0 border-b-2 border-solid border-brand text-center text-base font-bold text-brand"
    [attr.href]="featuresUrl"
    target="_blank"
  >
    {{ 'Compare all features' | translate }}
  </a>
</div>
